/* eslint-disable max-lines */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import {
  artboardBackgroundAtom,
  mobileSizePlacementOpenAtom,
  refineArtworkOpenAtom,
  useArtwork,
  useArtworkForm,
  useArtworkImage,
  useCalculatedArtworkDimensions,
  useEnhancedImageSource
} from "@design-lab/entities/artwork";
import { useArtworkWithPolling } from "@design-lab/entities/artwork/model/useArtworkWithPolling";
import {
  useCompositionsActions,
  useCompositionsList,
  useCurrentComposition
} from "@design-lab/entities/composition";
import { pinningSessionsAtom, useCancelPinningSession } from "@design-lab/entities/pinning";
import { isUserLoggedIn } from "@design-lab/entities/user";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import { useDesignProjectSync } from "@design-lab/features/design-project";
import { toFileObject, type PlacementFormPosition } from "@design-lab/shared";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackDesignLabPinningInstructionsRemoved } from "@design-lab/shared/lib/analytics";
import { useIsSample } from "@design-lab/shared/lib/config";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { Loader } from "@design-lab/shared/ui/loader";
import { useNotification } from "@design-lab/shared/ui/notification";
import { RefineArtboard, useArtboard } from "@design-lab/widgets/artboard";
import { useDesignLibrarySelection } from "@design-lab/widgets/design-lab-upload-artwork/model/useDesignLibrarySelection";
import { MyDesignLibrary } from "@design-lab/widgets/design-lab-upload-artwork/ui/MyDesignLibrary";
import { DESIGN_LAB_ACCEPT_CONFIG, validateDesignLabArtworkFile } from "common/artwork-file-types";

import { usePlacementZoomAndPan } from "@design-lab/features/canvas";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { useDesignLabArtworkAutoSizing } from "../model/useDesignLabArtworkAutoSizing";
import { ArtboardBase } from "./ArtboardBase";
import type { DesignLabArtboardProps } from "./ArtboardBase.types";

/**
 * Design Lab Artboard Wrapper
 *
 * Contains all Design Lab specific hooks, state management, and logic.
 * Passes data to ArtboardBase as props.
 */
// eslint-disable-next-line max-lines-per-function
export function DesignLabArtboard({ readonly = false }: DesignLabArtboardProps) {
  // Design Lab specific hooks
  const { form } = useArtworkForm();
  const { front, back } = useArtwork();
  const { deleteArtwork, uploadArtwork } = useArtworkImage();
  const { composition, placement } = useArtboard();
  const side = placement === "back" ? "back" : "front";
  const { source, artworkId } = useEnhancedImageSource({ side });
  const { isRestoring } = useDesignProjectSync();
  const isRefineOpen = useAtomValue(refineArtworkOpenAtom);
  const openRefine = useSetAtom(refineArtworkOpenAtom);
  const { isDesktop } = useResponsive();
  const isSample = useIsSample();
  const { error: notifyError } = useNotification();
  const isLoggedIn = isUserLoggedIn();

  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const [isMobileSizePlacementOpen, setIsMobileSizePlacementOpen] = useAtom(
    mobileSizePlacementOpenAtom
  );
  const { isSaveProjectVisible } = useSaveProject();
  const { setPan } = usePlacementZoomAndPan();
  const artboardRef = useRef<HTMLDivElement>(null);
  // Add this state and callbacks
  const [isProductBrowserOpen, setIsProductBrowserOpen] = useState(false);
  const [isMyDesignLibraryOpen, setIsMyDesignLibraryOpen] = useState(false);
  const [isPinningConfirmModalOpen, setIsPinningConfirmModalOpen] = useState(false);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const { mutate: cancelSession } = useCancelPinningSession();

  const handleOpenProductBrowser = useCallback(() => setIsProductBrowserOpen(true), []);

  const handleCloseProductBrowser = useCallback(() => setIsProductBrowserOpen(false), []);

  const handleLibraryUploadSuccess = useCallback(() => setIsMyDesignLibraryOpen(false), []);

  const { selectArtwork } = useDesignLibrarySelection({
    placement,
    onSuccess: handleLibraryUploadSuccess
  });

  const handleSelectArtworks = useCallback(
    (artworkIds: number[]) => {
      if (artworkIds.length > 0 && artworkIds[0] !== undefined) {
        selectArtwork(artworkIds[0]);
      }
    },
    [selectArtwork]
  );

  // Upload file handler - shared for both device and Canva uploads
  const handleFileUpload = useCallback(
    async (file: File, uploadSource: "upload" | "canva" = "upload") => {
      const { valid, message } = await validateDesignLabArtworkFile(file);
      if (!valid) {
        notifyError({ title: "Upload Failed", description: message });
        return;
      }

      try {
        await uploadArtwork({ file, side, source: uploadSource });
      } catch (error) {
        const errorData = (error as any)?.response?.data;
        const errorMessage =
          errorData?.validation_error ||
          errorData?.error ||
          `Failed to upload file${uploadSource === "canva" ? " from Canva" : ""}. Please try again.`;

        notifyError({
          title: "Upload Failed",
          description: errorMessage
        });
      }
    },
    [uploadArtwork, side, notifyError]
  );

  const { getInputProps, open } = useDropzone({
    accept: DESIGN_LAB_ACCEPT_CONFIG,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: async (droppedFiles: File[]) => {
      const file = droppedFiles[0];
      if (file) await handleFileUpload(file);
    }
  });

  const handleCanvaFileReady = useCallback(
    async (file: File) => handleFileUpload(file, "canva"),
    [handleFileUpload]
  );

  // Compositions for product carousel
  const { compositions } = useCompositionsList();
  const { currentComposition } = useCurrentComposition();
  const { selectCurrentComposition } = useCompositionsActions();

  const machine = side === "front" ? front : back;
  const surface = composition?.surfaces[placement];
  const frontSurface = composition?.surfaces?.front || composition?.surfaces?.frontLeftChest;
  const referenceBoundingBox =
    composition?.surfaces?.front?.boundingBox || composition?.surfaces?.frontLeftChest?.boundingBox;

  // Integrate fallback polling mechanism for BOTH front and back artworks
  const frontArtworkId = form.watch("front.artworkId");
  const backArtworkId = form.watch("back.artworkId");

  useArtworkWithPolling({
    artworkId: frontArtworkId ? Number(frontArtworkId) : undefined,
    startTime: front.state.context.startTime,
    machine: front,
    enabled: true
  });

  useArtworkWithPolling({
    artworkId: backArtworkId ? Number(backArtworkId) : undefined,
    startTime: back.state.context.startTime,
    machine: back,
    enabled: true
  });

  // Force form update when artwork finishes processing to trigger project save
  useEffect(() => {
    if (front.state.matches("finished") && front.state.context.artwork) {
      const currentArtworkId = form.getValues("front.artworkId");
      if (currentArtworkId) {
        // Touch the artwork ID field to trigger a form change and autosave
        form.setValue("front.artworkId", currentArtworkId, {
          shouldTouch: true,
          shouldDirty: true
        });
      }
    }
  }, [front.state, form]);

  useEffect(() => {
    if (back.state.matches("finished") && back.state.context.artwork) {
      const currentArtworkId = form.getValues("back.artworkId");
      if (currentArtworkId) {
        // Touch the artwork ID field to trigger a form change and autosave
        form.setValue("back.artworkId", currentArtworkId, { shouldTouch: true, shouldDirty: true });
      }
    }
  }, [back.state, form]);

  const artworkSizeBounds = useCalculatedArtworkDimensions(source.enhancedImage?.image, surface);

  // Auto-sizing logic
  useDesignLabArtworkAutoSizing({
    form,
    placement,
    surface,
    artworkImage: source.enhancedImage?.image,
    artworkSizeBounds
  });

  // Get all available surfaces for the current side
  const allSurfaces = useMemo(() => {
    const surfaces = [];

    if (placement === "back") {
      if (composition?.surfaces?.back)
        surfaces.push({ surface: composition.surfaces.back, placement });
    } else {
      if (composition?.surfaces?.front)
        surfaces.push({
          surface: composition.surfaces.front,
          placement: "front" as PlacementFormPosition
        });
      if (composition?.surfaces?.frontLeftChest)
        surfaces.push({
          surface: composition.surfaces.frontLeftChest,
          placement: "frontLeftChest" as PlacementFormPosition
        });
    }

    return surfaces;
  }, [composition?.surfaces, placement]);

  // Helper to create image preview from machine state
  const createImagePreview = useCallback((machineContext: typeof front.state.context) => {
    if (machineContext.file) {
      return toFileObject(machineContext.file, machineContext.originalImageUrl);
    }
    if (machineContext.artwork) {
      return { url: machineContext.artwork.originalImageUrl };
    }
    return undefined;
  }, []);

  // Image preview for loading boxes - separate for front and back
  const frontImagePreview = useMemo(
    () => createImagePreview(front.state.context),
    [
      createImagePreview,
      front.state.context.file,
      front.state.context.artwork,
      front.state.context.originalImageUrl
    ]
  );

  const backImagePreview = useMemo(
    () => createImagePreview(back.state.context),
    [
      createImagePreview,
      back.state.context.file,
      back.state.context.artwork,
      back.state.context.originalImageUrl
    ]
  );

  // Callbacks
  const onArtworkRemove = useCallback(() => {
    deleteArtwork(placement, artworkId);
    // Reset pan to center on mobile when artwork is removed
    if (!isDesktop) {
      setPan({ x: 0, y: 0 });
    }
  }, [deleteArtwork, placement, artworkId, isDesktop, setPan]);

  const handleCompositionSelect = useCallback(
    (id: string) => selectCurrentComposition(id),
    [selectCurrentComposition]
  );

  // Check if there's a pinning session for the current artwork
  const sessionForArtwork = artworkId ? pinningSessions[artworkId] : undefined;
  const hasActiveSession = sessionForArtwork?.state === "active";
  const hasCompletedSession = sessionForArtwork?.state === "completed";
  const hasPinningSession = hasActiveSession || hasCompletedSession;

  const handleEditDesignClick = useCallback(() => {
    if (hasPinningSession) {
      setIsPinningConfirmModalOpen(true);
      return;
    }
    openRefine(true);
  }, [hasPinningSession, openRefine]);

  const handlePinningConfirm = useCallback(() => {
    if (artworkId && sessionForArtwork) {
      const { sessionId } = sessionForArtwork;
      const artworkIdToRemove = artworkId;
      cancelSession(sessionId, {
        onSuccess: () => {
          trackDesignLabPinningInstructionsRemoved({
            artworkId: artworkIdToRemove,
            sessionId
          });
          setPinningSessions(prev => {
            const next = { ...prev };
            delete next[artworkIdToRemove];
            return next;
          });
          openRefine(true);
          setIsPinningConfirmModalOpen(false);
        }
      });
    } else {
      openRefine(true);
      setIsPinningConfirmModalOpen(false);
    }
  }, [artworkId, sessionForArtwork, cancelSession, setPinningSessions, openRefine]);

  // Loading states
  const frontLoading = front.state.hasTag("loading");
  const backLoading = back.state.hasTag("loading");
  const isLoading = machine.state.hasTag("loading");
  const hasArtworkId = form.watch(`${side}.artworkId`);

  // Reset pan to center on mobile when artwork becomes absent
  useEffect(() => {
    if (!isDesktop && !hasArtworkId) {
      setPan({ x: 0, y: 0 });
    }
  }, [hasArtworkId, isDesktop, setPan]);

  const LOADING_STEPS = [
    { caption: "Removing background" },
    { caption: "Enhancing resolution" },
    { caption: "Boosting vibrancy" },
    { caption: "Refining edges" }
  ];

  const createLoadingState = (
    loading: boolean,
    imagePreview: any,
    startTime: number | undefined
  ) =>
    loading
      ? {
          isLoading: true as const,
          imagePreview,
          startTime,
          estimatedDuration: ARTWORK_PROCESSING.ESTIMATED_DURATION,
          steps: LOADING_STEPS
        }
      : {
          isLoading: false,
          imagePreview
        };

  // Show loading state while restoring design project or if composition is not ready
  if (isRestoring || !composition || !surface) {
    return <Loader message="Creating preview" />;
  }

  // Render Refine Artwork overlay shell
  if (isRefineOpen && surface && referenceBoundingBox && frontSurface) {
    return (
      <Card className="h-full flex-1 overflow-hidden rounded-none lg:rounded-xl">
        <CardContent className="relative h-full p-0">
          <RefineArtboard
            frontSurface={frontSurface}
            referenceBoundingBox={referenceBoundingBox}
            surface={surface}
            enhancedImage={source.enhancedImage}
            placement={placement}
          />
        </CardContent>
      </Card>
    );
  }
  // Disable panning on mobile when no artwork is present
  const disablePanning = !isDesktop && !hasArtworkId;

  // Prepare props for ArtboardBase
  const canvasProps = {
    surface,
    referenceBoundingBox,
    placement,
    artworkImage: isLoading ? undefined : source.enhancedImage?.image,
    onArtworkRemove: isSaveProjectVisible ? undefined : onArtworkRemove,
    frontSurface,
    allSurfaces,
    labType: "design" as const,
    disablePanning
  };

  const controls = {
    showSideSwitcher: true,
    showZoom: true,
    showBackgroundToggle: true,
    showToolbar: isDesktop && !readonly,
    showProductBrowser: true, // Change this to true
    showRemoveBackground: false,
    showSizePlacement: true
  };

  const loadingState = createLoadingState(
    isLoading,
    side === "front" ? frontImagePreview : backImagePreview,
    machine.state.context.startTime
  );

  const frontLoadingState = createLoadingState(
    frontLoading,
    frontImagePreview,
    front.state.context.startTime
  );

  const backLoadingState = createLoadingState(
    backLoading,
    backImagePreview,
    back.state.context.startTime
  );

  const refineButton = {
    show: !isLoading,
    hasArtworkId: Boolean(hasArtworkId),
    onPress: handleEditDesignClick
  };

  const productCarousel = {
    show: true,
    compositions,
    selectedId: currentComposition?.id,
    onSelect: handleCompositionSelect,
    onAddMoreProduct: !isSample ? handleOpenProductBrowser : undefined
  };

  // Upload overlay for mobile - show when no artwork is present
  const shouldShowUploadOverlay = !isDesktop && machine.state.matches("initial") && !hasArtworkId;

  const uploadOverlay = shouldShowUploadOverlay
    ? {
        show: true,
        onDeviceUpload: open,
        onLibraryOpen: () => setIsMyDesignLibraryOpen(true),
        onCanvaFileReady: handleCanvaFileReady,
        getInputProps
      }
    : undefined;

  return (
    <>
      <div ref={artboardRef} className="h-full flex-1">
        <ArtboardBase
          readonly={readonly}
          canvasProps={canvasProps}
          controls={controls}
          backgroundType={backgroundType}
          onBackgroundChange={setBackgroundType}
          frontLoadingState={frontLoadingState}
          backLoadingState={backLoadingState}
          currentSide={side}
          loadingState={loadingState}
          zoomConfig={{ initialZoom: 2, maxZoom: 10 }}
          refineButton={refineButton}
          productCarousel={productCarousel}
          mobileSizePlacementOpen={isMobileSizePlacementOpen}
          onMobileSizePlacementChange={setIsMobileSizePlacementOpen}
          isProductBrowserOpen={isProductBrowserOpen}
          onProductBrowserClose={handleCloseProductBrowser}
          isSaveProject={isSaveProjectVisible}
          uploadOverlay={uploadOverlay}
        />
      </div>
      {isMyDesignLibraryOpen && isLoggedIn && (
        <MyDesignLibrary
          isOpen={isMyDesignLibraryOpen}
          onClose={() => setIsMyDesignLibraryOpen(false)}
          onSelectArtworks={handleSelectArtworks}
        />
      )}
      <ConfirmActionModal
        isOpen={isPinningConfirmModalOpen}
        onClose={() => setIsPinningConfirmModalOpen(false)}
        title="Remove Design Request?"
        message="This change will remove your design request, are you sure you want to proceed?"
        confirmText="Yes, Remove"
        cancelText="Keep Request"
        onConfirm={handlePinningConfirm}
      />
    </>
  );
}
